import { useState, useEffect, useMemo } from 'react';
import { Check, X, FileText, Eye, DollarSign, Calendar, User, Filter, Loader2, Search, ChevronUp, ChevronDown, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { expenseService } from '../services/expenseService';
import { expenseCategoryService } from '../services/expenseCategoryService';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';

const resolveFileUrl = (filePath?: string) => {
  if (!filePath) return '#';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${apiBase}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};

const parseOcrMetadata = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

interface Expense {
  id: string;
  userId: string;
  userName: string;
  category: string;
  amount: number;
  date: string;
  description: string;
  receiptUrl?: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  iban?: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  ocrData?: {
    merchant?: string;
    total?: number;
    date?: string;
    taxNumber?: string;
    rawText?: string;
    suggestedCategory?: string;
    summary?: {
      subtotal?: number | null;
      vat?: number | null;
      grandTotal?: number | null;
    };
    items?: Array<{ description: string; amount: number; raw: string }>;
  };
}

const hasReadableOcrData = (ocrData?: Expense['ocrData']) => {
  if (!ocrData) return false;
  return Boolean(
    String(ocrData.merchant || '').trim()
    || String(ocrData.taxNumber || '').trim()
    || String(ocrData.rawText || '').trim()
    || ocrData.items?.length
    || ocrData.summary?.grandTotal != null
    || ocrData.summary?.subtotal != null
    || ocrData.summary?.vat != null
  );
};

export function ExpenseApproval() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [previewExpense, setPreviewExpense] = useState<Expense | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<Array<{ value: string; label: string }>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Expense | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Partial<Record<keyof Expense, string>>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [datePreset, setDatePreset] = useState<'all' | 'last7' | 'last30' | 'thisMonth' | 'custom'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const columns: Array<{ key: keyof Expense; label: string }> = [
    { key: 'date', label: 'Tarih' },
    { key: 'userName', label: 'Personel' },
    { key: 'vehiclePlate', label: 'Çekici' },
    { key: 'trailerPlate', label: 'Dorse' },
    { key: 'iban', label: 'IBAN' },
    { key: 'category', label: 'Kategori' },
    { key: 'description', label: 'Açıklama' },
    { key: 'amount', label: 'Tutar' },
    { key: 'status', label: 'Durum' },
  ];

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, categoryResponse] = await Promise.all([
        expenseService.getExpenses(),
        expenseCategoryService.getCategories(),
      ]);
      setExpenseCategories((categoryResponse.data || categoryResponse || []).map((item: any) => ({ value: item.code, label: item.label })));
      const data = Array.isArray(response) ? response : response.data || [];
       setExpenses(data.map((item: any) => ({
         id: item.id?.toString() || '',
         userId: item.userId?.toString() || item.driverId?.toString() || '',
         userName: item.submitted_by_name || item.userName || item.full_name || item.driverName || item.submittedBy || 'Bilinmiyor',
         category: item.category || 'other',
        amount: parseFloat(item.amount) || 0,
        date: item.expense_date || item.date || item.expenseDate || '',
         description: item.description || item.note || '',
         receiptUrl: item.receipt_path || item.receiptUrl || item.receipt || undefined,
         vehiclePlate: item.vehicle_plate || '',
         trailerPlate: item.trailer_plate || '',
         iban: item.iban || '',
          status: item.status === 'approved' ? 'approved' : item.status === 'rejected' ? 'rejected' : 'pending',
        submittedAt: item.submittedAt || item.createdAt || new Date().toISOString(),
        approvedBy: item.approvedBy || undefined,
        approvedAt: item.approvedAt || undefined,
        rejectionReason: item.rejectionReason || item.notes || undefined,
         ocrData: (() => {
           const ocr = parseOcrMetadata(item.ocr_metadata) || item.ocrData;
           return ocr ? {
             merchant: ocr.merchant,
             total: ocr.total,
             date: ocr.date,
             taxNumber: ocr.taxNumber,
             rawText: ocr.rawText,
             suggestedCategory: ocr.suggestedCategory,
             summary: ocr.summary,
             items: ocr.items,
           } : undefined;
         })(),
      })));
    } catch (err) {
      console.error('Error loading expenses:', err);
      setError('Masraflar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (expense: Expense) => {
    try {
      await expenseService.updateExpenseStatus(expense.id, 'approved');
      setExpenses(prev => prev.map(e =>
        e.id === expense.id
          ? {
              ...e,
              status: 'approved' as const,
              approvedBy: user?.name,
              approvedAt: new Date().toISOString(),
            }
          : e
      ));
      setSelectedExpense(null);
    } catch (err) {
      console.error('Error approving expense:', err);
      setError('Masraf onaylanırken bir hata oluştu');
    }
  };

  const handleReject = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowRejectionModal(true);
  };

  const confirmRejection = async () => {
    if (!selectedExpense) return;

    try {
      await expenseService.updateExpenseStatus(selectedExpense.id, 'rejected', rejectionReason);
      setExpenses(prev => prev.map(e =>
        e.id === selectedExpense.id
          ? {
              ...e,
              status: 'rejected' as const,
              approvedBy: user?.name,
              approvedAt: new Date().toISOString(),
              rejectionReason: rejectionReason,
            }
          : e
      ));
      setShowRejectionModal(false);
      setRejectionReason('');
      setSelectedExpense(null);
    } catch (err) {
      console.error('Error rejecting expense:', err);
      setError('Masraf reddedilirken bir hata oluştu');
    }
  };

  const getCategoryLabel = (category: string) => expenseCategories.find((c) => c.value === category)?.label || category;

  const filteredExpenses = useMemo(() => expenses.filter((expense) => {
    const matchesStatus = filter === 'all' ? true : expense.status === filter;
    const rawDate = expense.date || '';
    const expenseDate = rawDate ? new Date(rawDate) : null;
    const now = new Date();
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    if (!expenseDate || Number.isNaN(expenseDate.getTime())) {
      if (datePreset !== 'all') return false;
    } else {
      if (datePreset === 'last7') {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 6);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'last30') {
        const start = new Date(todayStart);
        start.setDate(start.getDate() - 29);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'thisMonth') {
        const start = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
        if (expenseDate < start || expenseDate > todayEnd) return false;
      }
      if (datePreset === 'custom') {
        const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
        const end = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;
        if (start && expenseDate < start) return false;
        if (end && expenseDate > end) return false;
      }
    }

    const matchesSearch = !searchTerm.trim() || [
      expense.userName,
      expense.vehiclePlate || expense.trailerPlate || '',
      expense.iban || '',
      getCategoryLabel(expense.category),
      expense.description,
    ].join(' ').toLocaleLowerCase('tr-TR').includes(searchTerm.trim().toLocaleLowerCase('tr-TR'));
    const matchesColumnFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'category') return getCategoryLabel(expense.category).toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'));
      return String(expense[key as keyof Expense] ?? '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR'));
    });
    return matchesStatus && matchesSearch && matchesColumnFilters;
  }), [expenses, filter, searchTerm, filters, expenseCategories, datePreset, dateRange]);

  const sortedExpenses = useMemo(() => {
    if (!sortField) return filteredExpenses;
    return [...filteredExpenses].sort((a, b) => {
      const aVal = String(a[sortField] ?? '');
      const bVal = String(b[sortField] ?? '');
      const comparison = aVal.localeCompare(bVal, 'tr');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [filteredExpenses, sortField, sortOrder]);

  const paginatedExpenses = useMemo(
    () => sortedExpenses.slice((page - 1) * pageSize, page * pageSize),
    [sortedExpenses, page, pageSize]
  );

  const groupedExpenses = useMemo(() => {
    if (!groupBy) return { all: paginatedExpenses };
    return paginatedExpenses.reduce((acc: Record<string, Expense[]>, item) => {
      const key = String(item[groupBy as keyof Expense] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [paginatedExpenses, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(sortedExpenses as any[], `expense-approval-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'ExpenseApproval' });

  const handleSort = (field: keyof Expense) => {
    if (sortField === field) {
      setSortOrder((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortOrder('asc');
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setDatePreset('all');
    setDateRange({ start: '', end: '' });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const stats = {
    pending: expenses.filter(e => e.status === 'pending').length,
    approved: expenses.filter(e => e.status === 'approved').length,
    rejected: expenses.filter(e => e.status === 'rejected').length,
    totalPending: expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0),
    totalApproved: expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0),
  };

  const getStatusBadge = (status: Expense['status']) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Beklemede</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Onaylandı</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Reddedildi</span>;
    }
  };

  useEffect(() => {
    setPage(1);
  }, [filter, searchTerm, filters]);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Masraf Onayı</h1>
        <p className="text-sm text-gray-600 mt-1">Personel masraflarını inceleyin ve onaylayın</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bekleyen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bekleyen Tutar</p>
              <p className="text-2xl font-bold text-gray-900">₺{stats.totalPending.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Onaylanan</p>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Reddedilen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <div className="flex gap-2">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {status === 'pending' ? 'Bekleyen' :
               status === 'approved' ? 'Onaylanan' :
               status === 'rejected' ? 'Reddedilen' : 'Tümü'}
            </button>
          ))}
        </div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Personel, plaka, kategori veya açıklama ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="outline-none" />
          </div>
          <button onClick={() => setShowFilters((current) => !current)} className={`workiom-outline-button ${showFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
            <Filter className="w-4 h-4" /> Filtrele
          </button>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value as typeof datePreset)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
            <option value="all">Tüm Tarihler</option>
            <option value="last7">Son 7 Gün</option>
            <option value="last30">Son 30 Gün</option>
            <option value="thisMonth">Bu Ay</option>
            <option value="custom">Özel Tarih</option>
          </select>
          {datePreset === 'custom' && (
            <>
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange((current) => ({ ...current, start: e.target.value }))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" />
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange((current) => ({ ...current, end: e.target.value }))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700" />
            </>
          )}
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">Excele Aktar</button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="workiom-outline-button">
              <XCircle className="w-4 h-4" /> Filtreleri Temizle
            </button>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGrouping((current) => !current)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
              <Filter className="w-4 h-4" /> Gruplandır
            </button>
            {showGrouping && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">Gruplama Yok</option>
                <option value="status">Durum</option>
                <option value="category">Kategori</option>
                <option value="userName">Personel</option>
                 <option value="vehiclePlate">Çekici</option>
                 <option value="trailerPlate">Dorse</option>
                 <option value="iban">IBAN</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="workiom-grid-wrap border border-gray-200 bg-white">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Masraflar yükleniyor...</span>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadExpenses}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Tekrar Dene
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="workiom-grid-table text-sm">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none ${column.key === 'amount' ? 'text-right' : 'text-left'}`} onClick={() => handleSort(column.key)}>
                      <div className={`flex items-center gap-2 ${column.key === 'amount' ? 'justify-end' : ''}`}>{column.label}{sortField === column.key && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}</div>
                    </th>
                  ))}
                  <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase">Fiş</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                </tr>
                {showFilters && (
                  <tr>
                    {columns.map((column) => (
                      <th key={`filter-${column.key}`} className="px-2 py-1.5">
                        <input value={filters[column.key] || ''} onChange={(e) => setFilters((current) => ({ ...current, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" />
                      </th>
                    ))}
                    <th></th>
                    <th></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {Object.entries(groupedExpenses).flatMap(([groupKey, groupItems]) => {
                  const rows: JSX.Element[] = [];
                  if (groupBy) {
                    rows.push(
                      <tr key={`group-${groupKey}`} className="bg-gray-100">
                        <td colSpan={columns.length + 2} className="px-3 py-2">
                          <button type="button" onClick={() => setCollapsedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            {collapsedGroups[groupKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            <span>{groupKey}</span>
                            <span className="text-gray-500">({groupItems.length})</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  if (!collapsedGroups[groupKey]) {
                    rows.push(...groupItems.map((expense) => (
                <tr key={expense.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 align-middle">
                    <span className="text-sm text-gray-900">
                      {expense.date ? new Date(expense.date).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{expense.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <span className="text-sm text-gray-900">{expense.vehiclePlate || '-'}</span>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <span className="text-sm text-gray-900">{expense.trailerPlate || '-'}</span>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <span className="text-sm font-mono text-gray-900">{expense.iban || '-'}</span>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <span className="text-sm text-gray-900">
                       {expenseCategories.find(c => c.value === expense.category)?.label || expense.category}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <div>
                      <p className="text-sm text-gray-900">{expense.description}</p>
                      {hasReadableOcrData(expense.ocrData) && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ OCR: {expense.ocrData.merchant} - VKN: {expense.ocrData.taxNumber}
                        </p>
                      )}
                      {!hasReadableOcrData(expense.ocrData) && expense.receiptUrl && (
                        <p className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                          OCR okunamadı
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle text-right">
                    <span className="text-sm font-semibold text-gray-900">₺{expense.amount.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-2 align-middle text-center">
                    {getStatusBadge(expense.status)}
                  </td>
                  <td className="px-4 py-2 align-middle text-center">
                    <div className="flex items-center justify-center gap-2">
                      {expense.receiptUrl ? (
                        <button
                          type="button"
                          onClick={() => setPreviewExpense(expense)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                          title="Fişi Gör"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                      {expense.ocrData ? (
                        <button
                          type="button"
                          onClick={() => { setSelectedExpense(expense); setShowDetailModal(true); }}
                          className={`inline-flex items-center gap-1 text-xs ${hasReadableOcrData(expense.ocrData) ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-700 hover:text-amber-800'}`}
                        >
                          OCR
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {expense.status === 'pending' ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(expense)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Onayla"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(expense)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Reddet"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end text-xs text-gray-500">
                        {expense.approvedBy}
                      </div>
                    )}
                  </td>
                </tr>
                    )));
                  }

                  return rows;
                })}
              </tbody>
            </table>
          </div>
        )}

        {filteredExpenses.length === 0 && !loading && !error && (
          <div className="text-center py-12 text-gray-500">
            <p>
              {filter === 'pending' ? 'Bekleyen masraf yok' : 'Masraf bulunamadı'}
            </p>
          </div>
        )}
      </div>
      {!loading && !error && (
        <div className="mb-6 flex items-center justify-end gap-3">
          <WorkiomTablePagination page={page} pageSize={pageSize} totalRows={filteredExpenses.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        </div>
      )}

      {/* Rejection Modal */}
      {showDetailModal && selectedExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Masraf OCR Detayı</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)] gap-6">
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900 mb-3">Fiş Görseli</p>
                  {selectedExpense.receiptUrl ? (
                    <div className="rounded-lg overflow-hidden border border-slate-200 bg-white">
                      {selectedExpense.receiptUrl.match(/\.pdf($|\?)/i) ? (
                        <iframe src={resolveFileUrl(selectedExpense.receiptUrl)} className="w-full h-[28rem]" title="Fiş PDF Önizleme" />
                      ) : (
                        <img src={resolveFileUrl(selectedExpense.receiptUrl)} alt="Fiş" className="w-full max-h-[28rem] object-contain bg-white" />
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">Fiş görseli bulunmuyor.</div>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div>Çekici: <strong>{selectedExpense.vehiclePlate || '-'}</strong></div>
                  <div>Dorse: <strong>{selectedExpense.trailerPlate || '-'}</strong></div>
                  <div>IBAN: <strong>{selectedExpense.iban || '-'}</strong></div>
                  <div>İşyeri: <strong>{selectedExpense.ocrData?.merchant || '-'}</strong></div>
                  <div>Tarih: <strong>{selectedExpense.ocrData?.date || '-'}</strong></div>
                  <div>VKN: <strong>{selectedExpense.ocrData?.taxNumber || '-'}</strong></div>
                  <div>OCR Kategori Önerisi: <strong>{expenseCategories.find((c) => c.value === selectedExpense.ocrData?.suggestedCategory)?.label || selectedExpense.ocrData?.suggestedCategory || '-'}</strong></div>
                  <div>Ara Toplam: <strong>{selectedExpense.ocrData?.summary?.subtotal != null ? `${Number(selectedExpense.ocrData.summary.subtotal).toLocaleString('tr-TR')} ₺` : '-'}</strong></div>
                  <div>KDV: <strong>{selectedExpense.ocrData?.summary?.vat != null ? `${Number(selectedExpense.ocrData.summary.vat).toLocaleString('tr-TR')} ₺` : '-'}</strong></div>
                  <div>Genel Toplam: <strong>{selectedExpense.ocrData?.summary?.grandTotal != null ? `${Number(selectedExpense.ocrData.summary.grandTotal).toLocaleString('tr-TR')} ₺` : '-'}</strong></div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900 mb-2">Satır Satır Ürün / Tutar</p>
                  <div className="space-y-2 max-h-56 overflow-y-auto text-sm">
                    {selectedExpense.ocrData?.items?.length ? selectedExpense.ocrData.items.map((item, index) => (
                      <div key={`${item.raw}-${index}`} className="flex items-start justify-between gap-3 border-b border-slate-200 pb-2 last:border-b-0">
                        <span>{item.description}</span>
                        <span className="font-medium whitespace-nowrap">{Number(item.amount).toLocaleString('tr-TR')} ₺</span>
                      </div>
                    )) : <div className="text-gray-500">Kırılım bulunamadı.</div>}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900 mb-2">Tam OCR İçeriği</p>
                  <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 max-h-72 overflow-y-auto">{selectedExpense.ocrData?.rawText || '-'}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Masrafı Reddet</h2>
              <button
                onClick={() => setShowRejectionModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>{selectedExpense?.userName}</strong> tarafından gönderilen masrafı reddediyorsunuz.
                </p>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-900">
                    {selectedExpense?.description}
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    ₺{selectedExpense?.amount.toFixed(2)}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reddetme Nedeni *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  placeholder="Masrafın neden reddedildiğini açıklayın..."
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRejectionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={confirmRejection}
                  disabled={!rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reddet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Yüklenen Evrak</h2>
                <p className="text-sm text-gray-500 mt-1">{previewExpense.userName} - {previewExpense.category}</p>
              </div>
              <button onClick={() => setPreviewExpense(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {previewExpense.receiptUrl ? (
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-white">
                  {previewExpense.receiptUrl.match(/\.pdf($|\?)/i) ? (
                    <iframe src={resolveFileUrl(previewExpense.receiptUrl)} className="w-full h-[70vh]" title="Fiş Önizleme" />
                  ) : (
                    <img src={resolveFileUrl(previewExpense.receiptUrl)} alt="Yüklenen Evrak" className="w-full max-h-[70vh] object-contain bg-white" />
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">Yüklü evrak bulunmuyor.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
